import * as React from 'react';
import {
    CheckboxVisibility,
    DetailsList,
    Fabric,
    IGroup,
    IObjectWithKey,
    ISelection,
    ScrollablePane,
    Selection,
    SelectionMode,
    Sticky,
    StickyPositionType,
} from '@fluentui/react';
import { getTheme, ITheme, mergeStyleSets } from '@fluentui/react/lib/Styling';
import { KundeFilter, useKundeList } from 'api/kunde';
import { Ansprechpartner } from 'api/ansprechpartner';
import { LoadingIndicator } from 'layout/LoadingIndicator/LoadingIndicator';

const theme: ITheme = getTheme();

const styles = mergeStyleSets({
    container: {
        position: 'relative',
        display: 'grid',
        gridTemplateRows: 'max-content 250px 1fr',
        gridRowGap: 5,
    },
    mailListHeader: {
        fontSize: 16,
        marginLeft: 6,
        marginTop: 6,
        marginBottom: 6,
    },
    copyMailList: {
        fontSize: 10,
        marginLeft: 15,
    },
    mailList: {
        overflow: 'scroll',
    },
    list: {
        position: 'relative',
        // overflow: 'scroll',
    },
    defaultCell: {
        fontSize: theme.fonts.medium.fontSize,
    },
    row: {
        backgroundColor: 'lightblue !important',
    },
    select: {
        cursor: 'pointer',
    },
});

interface KundeMailingProps {
    kundeFilter: KundeFilter | null;
}

export const KundeMailing: React.FC<KundeMailingProps> = ({ kundeFilter }) => {
    const mailingFilter = React.useMemo<KundeFilter>(() => {
        return {
            ...kundeFilter,
            withAnsprechpartnerList: true,
        };
    }, [kundeFilter]);

    const [selectedAnsprechpartner, setSelectedAnsprechpartner] = React.useState<Ansprechpartner[]>([]);

    const uniqueMails = React.useMemo(() => {
        const dict: { [key: string]: number } = {};

        selectedAnsprechpartner.forEach((ap) => {
            if (ap.mail && ap.mail.trim() !== '') {
                dict[ap.mail.trim()] = 1;
            }
        });

        return Object.keys(dict);
    }, [selectedAnsprechpartner]);

    const kundeList = useKundeList(mailingFilter, { enabled: false });

    React.useEffect(() => {
        kundeList.refetch();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mailingFilter]);

    const selection = React.useRef<ISelection<IObjectWithKey>>(
        new Selection({
            selectionMode: SelectionMode.multiple,
            getKey: (item) => String((item as Ansprechpartner).kNr + '.' + (item as Ansprechpartner).nr),
            onSelectionChanged: () => {
                setSelectedAnsprechpartner(selection.current.getSelection() as Ansprechpartner[]);
            },
        }),
    );

    const { groups, items } = React.useMemo<{ groups: IGroup[]; items: Ansprechpartner[] }>(() => {
        const localGroups: IGroup[] = [];
        const localItems: Ansprechpartner[] = [];

        if (!kundeList.data || kundeList.data.length === 0) {
            return { groups: [], items: [] };
        }

        let index = 0;
        kundeList.data.forEach((kunde) => {
            let count = 0;
            const startIndex = index;
            (kunde.ansprechpartnerList || []).forEach((ansprechpartner) => {
                if (ansprechpartner.mail) {
                    localItems.push(ansprechpartner);
                    index += 1;
                    count += 1;
                }
            });

            if (count > 0) {
                localGroups.push({
                    key: kunde.nr.toString(),
                    name: kunde.name1 + ' ' + kunde.name2 + '   (Nr: ' + kunde.nr + ')',
                    startIndex,
                    count,
                });
            }
        });

        selection.current.setItems((localItems as never) as IObjectWithKey[], true);

        return { groups: localGroups, items: localItems };
    }, [kundeList]);

    const textareaRef = React.useRef<HTMLTextAreaElement>(null);

    const onRenderDetailsHeader = React.useCallback(
        (detailsHeaderProps, defaultRender) => (
            <Sticky stickyPosition={StickyPositionType.Header}>
                {defaultRender
                    ? defaultRender({
                          ...detailsHeaderProps,
                      } as never)
                    : null}
            </Sticky>
        ),
        [],
    );

    const onRenderRow = React.useCallback((props, defaultRender?): JSX.Element => {
        return defaultRender({
            ...props,
            onClick: () => {
                if (props?.itemIndex !== undefined) {
                    selection.current.toggleIndexSelected(props.itemIndex);
                }
            },
        });
    }, []);

    const copyMailList = () => {
        navigator.clipboard.writeText(uniqueMails.join('; '));
    };

    return (
        <Fabric className={styles.container}>
            {kundeList.isLoading || kundeList.isFetching ? (
                <LoadingIndicator />
            ) : (
                <>
                    <div className={styles.mailListHeader}>
                        Ausgewählte E-Mail Adressen
                        <button className={styles.copyMailList} onClick={copyMailList}>
                            Kopieren...
                        </button>
                    </div>
                    <textarea
                        className={styles.mailList}
                        ref={textareaRef}
                        readOnly
                        value={
                            uniqueMails.length === 0
                                ? 'Keine Ansprechpartner mit E-Mail Adressen ausgewählt.'
                                : uniqueMails.join('; ')
                        }
                    />
                    <div className={styles.list}>
                        <ScrollablePane>
                            <DetailsList
                                items={items}
                                compact
                                selectionMode={SelectionMode.multiple}
                                selectionPreservedOnEmptyClick
                                checkboxVisibility={CheckboxVisibility.always}
                                selection={selection.current}
                                groups={groups}
                                disableSelectionZone
                                groupProps={{
                                    showEmptyGroups: true,
                                }}
                                onRenderDetailsHeader={onRenderDetailsHeader}
                                onRenderRow={onRenderRow}
                                columns={[
                                    {
                                        key: 'name',
                                        name: 'Name',
                                        fieldName: 'name',
                                        minWidth: 200,
                                        maxWidth: 300,
                                        isResizable: true,
                                        className: styles.defaultCell,
                                    },
                                    {
                                        key: 'position',
                                        name: 'Position',
                                        fieldName: 'position',
                                        minWidth: 200,
                                        maxWidth: 300,
                                        isResizable: true,
                                        className: styles.defaultCell,
                                    },
                                    {
                                        key: 'mail',
                                        name: 'Mail',
                                        fieldName: 'mail',
                                        minWidth: 150,
                                        maxWidth: 300,
                                        isResizable: true,
                                        className: styles.defaultCell,
                                    },
                                    {
                                        key: 'telefon',
                                        name: 'Telefon',
                                        fieldName: 'telefon',
                                        minWidth: 50,
                                        maxWidth: 200,
                                        isResizable: true,
                                        className: styles.defaultCell,
                                    },
                                    {
                                        key: 'mobil',
                                        name: 'Mobil',
                                        fieldName: 'mobil',
                                        minWidth: 50,
                                        maxWidth: 200,
                                        isResizable: true,
                                        className: styles.defaultCell,
                                    },
                                ]}
                            />
                        </ScrollablePane>
                    </div>
                </>
            )}
        </Fabric>
    );
};
